import { useEffect, useRef } from 'react';
import type { AchievementKind } from '@busy/api';

// Per-kind celebration overlay. One canvas-2d engine, distinct particle
// systems — chosen by `kind` to match the achievement's identity:
//
//   streak           → fire     (rising orange/red flames from the bottom)
//   clients          → confetti (rotating rectangles falling under gravity)
//   cuts             → fireworks (shells launch + burst into radial sparks)
//   revenue          → jackpot   (gold coins + green dollar tiles raining down)
//   lessons          → electric  (jagged lightning bolts flickering across)
//   transformations  → flashes   (full-screen camera flashes + radial bursts)
//   timekeeper       → confetti  (reuses clients — discipline = celebration)
//
// All animations use the brand palette so the celebration feels like part of
// the app rather than a stock effect. Self-cleaning — unmounts itself when
// the duration elapses or every particle has scrolled offscreen.

// Brand identity — navy / gold / red / white. Green and orange are reserved
// for status semantics elsewhere in the app (success / warning), so the
// celebration effects stay strictly brand-identity to read as the brand
// itself rather than a status nudge. Fire leans on the warm end of the
// gold ramp + reds; the human eye reads that as flame fine without
// dropping in a stock orange.
const NAVY_900   = '#0c1530';
const NAVY_700   = '#1a2954';
const GOLD_500   = '#d4a942';
const GOLD_400   = '#e8c66a';
const GOLD_300   = '#f3dc99';
const GOLD_DEEP  = '#9c7d28'; // dark-gold ring on coins
const RED_600    = '#c8102e';
const RED_400    = '#e63950';
const WHITE      = '#ffffff';
const CREAM      = '#fbf4dd'; // gold-50 — slightly warm white for ticket bg

// Tier scaling — every kind ships 8 thresholds (4 tiers × 2). Intensity 1-8
// maps to the threshold's index so the higher you climb, the more violent the
// celebration: more particles, faster emit, longer duration, brighter glow.
// Default 4 keeps prior single-level callsites looking like a mid-tier unlock.
const MIN_INTENSITY = 1;
const MAX_INTENSITY = 8;
function clampIntensity(n: number | undefined): number {
  const v = Math.round(n ?? 4);
  if (v < MIN_INTENSITY) return MIN_INTENSITY;
  if (v > MAX_INTENSITY) return MAX_INTENSITY;
  return v;
}
// 0..1 progress along the intensity ramp — used to lerp every per-kind knob.
function ramp(intensity: number): number {
  return (intensity - MIN_INTENSITY) / (MAX_INTENSITY - MIN_INTENSITY);
}
function lerp(min: number, max: number, t: number): number {
  return min + (max - min) * t;
}

// Generic particle shape — different effects use different fields.
type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;       // 0..1, ticks from 1 (born) → 0 (dead)
  decay: number;      // amount subtracted from life per frame
  size: number;
  rot: number;
  rotV: number;
  color: string;
  shape: 'rect' | 'circle' | 'streamer' | 'coin' | 'bill' | 'spark';
};

export function BadgeEffect({
  kind,
  intensity,
}: {
  kind: AchievementKind;
  /** 1-8 — maps to threshold tier so higher milestones feel more dramatic. */
  intensity?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const level = clampIntensity(intensity);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const r = ramp(level);
    // Higher tiers run longer so the dense-particle look has time to register.
    const DURATION_MS = Math.round(lerp(2400, 4400, r));

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    function resize() {
      if (!canvas || !ctx) return;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener('resize', resize);

    const W = window.innerWidth;
    const H = window.innerHeight;
    const particles: Particle[] = [];
    const bolts: Bolt[] = []; // electric only
    const flashes: Flash[] = []; // transformations only
    let nextFireworkAt = 0;
    let nextBoltAt = 0;
    let nextFireEmitAt = 0;
    let nextFlashAt = 0;

    // Seed initial particles for effects that don't continuously emit.
    if (kind === 'clients' || kind === 'timekeeper') seedConfetti(particles, W, r);
    // Transformations: open with an immediate full-screen flash so the
    // moment hits the eye before the gentler bursts roll in.
    if (kind === 'transformations') flashes.push(makeFlash(W, H, r, true));

    const start = performance.now();
    let raf = 0;

    function frame(now: number) {
      if (!canvas || !ctx) return;
      const elapsed = now - start;
      const t = elapsed / DURATION_MS;
      // Use lighter composite for glow effects; switch back per particle.
      ctx.clearRect(0, 0, W, H);
      const fade = t < 0.85 ? 1 : Math.max(0, 1 - (t - 0.85) / 0.15);

      // Spawning logic per kind. Per-kind cadence + counts scale with `r`
      // (0 at intensity 1 → 1 at intensity 8) so high tiers feel sustained
      // and dense rather than just longer.
      if (kind === 'streak' && now > nextFireEmitAt && t < 0.85) {
        emitFire(particles, W, H, r);
        // 60ms between emits at L1, 18ms at L8 — denser flame wall as tier rises.
        nextFireEmitAt = now + lerp(60, 18, r);
      }
      if (kind === 'cuts' && now > nextFireworkAt && t < 0.85) {
        emitFirework(particles, W, H, r);
        // 1200ms gap at L1, 320ms at L8.
        nextFireworkAt = now + lerp(1200, 320, r) + Math.random() * 200;
      }
      if (kind === 'lessons') {
        if (now > nextBoltAt && t < 0.85) {
          // High tiers fire 2-3 bolts per spawn — a flickering electric storm.
          const burst = 1 + Math.floor(r * 2.4); // 1 → 3
          for (let i = 0; i < burst; i++) bolts.push(makeBolt(W, H, r));
          nextBoltAt = now + lerp(280, 90, r) + Math.random() * 80;
        }
        // Tick existing bolts
        for (const b of bolts) b.life -= 0.08;
        for (let i = bolts.length - 1; i >= 0; i--) {
          if (bolts[i]!.life <= 0) bolts.splice(i, 1);
        }
      }
      if (kind === 'revenue') {
        // Spawn rate ramps from 0.25 (L1, light shower) to 0.95 (L8, downpour).
        const spawnP = lerp(0.25, 0.95, r);
        if (Math.random() < spawnP && t < 0.85) {
          particles.push(makeJackpot(W, r));
        }
      }
      if (kind === 'transformations') {
        if (now > nextFlashAt && t < 0.85) {
          flashes.push(makeFlash(W, H, r, false));
          // 700ms gap at L1, 220ms at L8 — paparazzi cadence ramps with tier.
          nextFlashAt = now + lerp(700, 220, r) + Math.random() * 120;
        }
        for (const f of flashes) f.life -= f.decay;
        for (let i = flashes.length - 1; i >= 0; i--) {
          if (flashes[i]!.life <= 0) flashes.splice(i, 1);
        }
      }

      // Update + draw particles
      const gravity = kind === 'streak' ? -0.05 : 0.18; // fire rises
      let alive = 0;
      for (const p of particles) {
        // Per-shape physics tweaks
        if (p.shape === 'spark') {
          // Fireworks sparks: gentler gravity, faster decay
          p.vy += 0.12;
          p.vx *= 0.985;
        } else {
          p.vy += gravity;
          p.vx *= 0.995;
        }
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.rotV;
        p.life -= p.decay;

        if (p.life > 0 && p.y < H + 80 && p.y > -80) alive++;

        if (p.life <= 0) continue;
        drawParticle(ctx, p, fade);
      }
      // Drop dead particles to keep array small
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        if (!p) continue;
        if (p.life <= 0 || p.y > H + 100) particles.splice(i, 1);
      }

      // Draw bolts (lessons)
      if (bolts.length > 0) drawBolts(ctx, bolts, fade, r);
      // Draw flashes (transformations) — drawn after particles so the burst
      // overlay sits on top.
      if (flashes.length > 0) drawFlashes(ctx, flashes, W, H, fade);

      const stillRunning =
        elapsed < DURATION_MS &&
        (alive > 0 ||
          bolts.length > 0 ||
          flashes.length > 0 ||
          (kind === 'streak' && t < 0.85) ||
          (kind === 'cuts' && t < 0.85) ||
          (kind === 'revenue' && t < 0.85) ||
          (kind === 'lessons' && t < 0.85) ||
          (kind === 'transformations' && t < 0.85));
      if (stillRunning) {
        raf = requestAnimationFrame(frame);
      } else {
        ctx.clearRect(0, 0, W, H);
      }
    }
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [kind, level]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="fixed inset-0 z-[60] pointer-events-none"
    />
  );
}

/* ============================================================================
 * Per-kind emitters
 * ============================================================================ */

function seedConfetti(out: Particle[], W: number, r: number) {
  const COLORS = [GOLD_500, GOLD_400, GOLD_300, RED_600, NAVY_700, NAVY_900, WHITE];
  // Particle count scales hard — confetti is the kind users tie most directly
  // to "amount of celebration." 50 at L1 → 240 at L8.
  const total = Math.round(lerp(50, 240, r));
  for (let i = 0; i < total; i++) {
    const fromLeft = Math.random() < 0.5;
    const xJitter = Math.random() * W * 0.5;
    const x = fromLeft ? xJitter : W - xJitter;
    const isStreamer = Math.random() < 0.25;
    out.push({
      x,
      y: -20 - Math.random() * 80,
      vx: (fromLeft ? 1 : -1) * (1 + Math.random() * 2.5),
      vy: 2 + Math.random() * 3,
      size: isStreamer ? 3 + Math.random() * 2 : 6 + Math.random() * 6,
      rot: Math.random() * Math.PI * 2,
      rotV: (Math.random() - 0.5) * 0.3,
      color: COLORS[Math.floor(Math.random() * COLORS.length)] ?? WHITE,
      shape: isStreamer ? 'streamer' : 'rect',
      life: 1,
      decay: 0.0015,
    });
  }
}

function emitFire(out: Particle[], W: number, H: number, r: number) {
  // Spawn flame particles per emit along the bottom edge. Pulls from the warm
  // end of the brand ramp — gold-300 → gold-500 → red-400 → red-600 reads as
  // flame without needing an off-brand orange. Higher tiers spawn more
  // particles per emit AND make them larger + longer-lived, so the wall of
  // flame visibly climbs higher rather than just being more numerous.
  const FIRE_COLORS = [GOLD_300, GOLD_400, GOLD_500, RED_400, RED_600];
  const perEmit = Math.round(lerp(2, 9, r));
  const sizeBase = lerp(12, 22, r);
  const sizeJitter = lerp(14, 24, r);
  const decayBase = lerp(0.025, 0.012, r);
  const upwardBase = lerp(2.4, 4.2, r);
  for (let i = 0; i < perEmit; i++) {
    out.push({
      x: Math.random() * W,
      y: H + 20,
      vx: (Math.random() - 0.5) * 0.6,
      vy: -upwardBase - Math.random() * 2.5, // upward
      size: sizeBase + Math.random() * sizeJitter,
      rot: 0,
      rotV: 0,
      color: FIRE_COLORS[Math.floor(Math.random() * FIRE_COLORS.length)] ?? GOLD_500,
      shape: 'circle',
      life: 1,
      decay: decayBase + Math.random() * 0.008,
    });
  }
}

function emitFirework(out: Particle[], W: number, _H: number, r: number) {
  // Pick a burst point in the upper half, fire a radial spray. High tiers
  // chain 1-3 bursts in a single emit and pack each burst with more sparks,
  // so a tier-8 cuts unlock looks like a finale rather than a single shell.
  const PALETTE = [GOLD_500, GOLD_400, RED_600, WHITE, NAVY_700];
  const shells = 1 + Math.floor(r * 2.4); // 1 → 3
  const sparksPerBurst = Math.round(lerp(20, 48, r));
  const sparkSizeBase = lerp(2.6, 4.6, r);
  for (let s = 0; s < shells; s++) {
    const cx = W * 0.15 + Math.random() * W * 0.7;
    const cy = window.innerHeight * 0.18 + Math.random() * window.innerHeight * 0.35;
    const burstColor = PALETTE[Math.floor(Math.random() * PALETTE.length)] ?? GOLD_500;
    const count = sparksPerBurst + Math.floor(Math.random() * 10);
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.15;
      const speed = 3 + Math.random() * 3;
      out.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: sparkSizeBase + Math.random() * 2,
        rot: 0,
        rotV: 0,
        color: burstColor,
        shape: 'spark',
        life: 1,
        decay: 0.022 + Math.random() * 0.01,
      });
    }
  }
}

function makeJackpot(W: number, r: number): Particle {
  // ~70% gold coins, 30% cream tickets with a gold "$" stamp. Tickets read
  // as money/winnings without needing an off-brand green. High-tier revenue
  // unlocks scale the artwork up — bigger coins, fatter bills — so the rain
  // reads as heavier (not just denser).
  const isCoin = Math.random() < 0.7;
  const coinBase = lerp(9, 14, r);
  const billBase = lerp(13, 19, r);
  return {
    x: Math.random() * W,
    y: -30 - Math.random() * 100,
    vx: (Math.random() - 0.5) * 1.5,
    vy: 1.5 + Math.random() * 2,
    size: isCoin ? coinBase + Math.random() * 6 : billBase + Math.random() * 8,
    rot: Math.random() * Math.PI * 2,
    rotV: (Math.random() - 0.5) * 0.18,
    color: isCoin ? GOLD_500 : CREAM,
    shape: isCoin ? 'coin' : 'bill',
    life: 1,
    decay: 0.0014,
  };
}

/* ============================================================================
 * Lightning bolts (lessons)
 * ============================================================================ */

type Bolt = { points: Array<{ x: number; y: number }>; life: number };

function makeBolt(W: number, H: number, r: number): Bolt {
  // Random start near top, jagged path down. Some bolts go diagonal across.
  // Higher tiers add more segments → more violent jagged path.
  const horizontal = Math.random() < 0.4;
  const startX = horizontal ? -20 : W * 0.1 + Math.random() * W * 0.8;
  const startY = horizontal ? H * 0.2 + Math.random() * H * 0.4 : -10;
  const endX = horizontal ? W + 20 : startX + (Math.random() - 0.5) * 200;
  const endY = horizontal ? startY + (Math.random() - 0.5) * 200 : H * 0.6 + Math.random() * H * 0.3;
  const segMin = Math.round(lerp(5, 9, r));
  const segments = segMin + Math.floor(Math.random() * 3);
  const points: Bolt['points'] = [{ x: startX, y: startY }];
  for (let i = 1; i < segments; i++) {
    const t = i / segments;
    const baseX = startX + (endX - startX) * t;
    const baseY = startY + (endY - startY) * t;
    points.push({ x: baseX + (Math.random() - 0.5) * 60, y: baseY + (Math.random() - 0.5) * 60 });
  }
  points.push({ x: endX, y: endY });
  return { points, life: 1 };
}

/* ============================================================================
 * Camera flashes (transformations)
 * ============================================================================ */

// A flash is either a full-screen white wash (the "open the eyes" moment) or a
// localized radial burst at a random spot — the paparazzi popping bulbs from
// every angle. Each one fades quickly. Higher tiers add a faint gold tint to
// the radial bursts so the room feels lit by gold spotlights, not just white.
type Flash = {
  x: number;
  y: number;
  /** Outer radius this flash will reach at peak. */
  radius: number;
  /** 0..1 — ticks down to 0. */
  life: number;
  decay: number;
  /** True for the opening full-screen wash. False for the radial bursts. */
  fullscreen: boolean;
  /** 0..1 — gold tint mix; 0 is pure white. */
  tint: number;
};

function makeFlash(W: number, H: number, r: number, fullscreen: boolean): Flash {
  if (fullscreen) {
    return {
      x: W / 2,
      y: H / 2,
      radius: Math.max(W, H),
      life: 1,
      // Slower decay than radial bursts — opening flash lingers slightly so the
      // eye registers it before the bursts start.
      decay: 0.06,
      fullscreen: true,
      tint: 0,
    };
  }
  // Radial burst — random spot, larger + warmer at higher tiers.
  const radius = lerp(180, 360, r) + Math.random() * 100;
  return {
    x: Math.random() * W,
    y: Math.random() * H * 0.85,
    radius,
    life: 1,
    decay: lerp(0.10, 0.07, r),
    fullscreen: false,
    tint: lerp(0.0, 0.35, r) * Math.random(),
  };
}

function drawFlashes(ctx: CanvasRenderingContext2D, flashes: Flash[], W: number, H: number, fade: number) {
  ctx.save();
  for (const f of flashes) {
    const a = Math.max(0, f.life) * fade;
    if (f.fullscreen) {
      ctx.fillStyle = `rgba(255,255,255,${a})`;
      ctx.fillRect(0, 0, W, H);
      continue;
    }
    // Radial gradient burst — bright white core fading to transparent. A
    // touch of gold (212,169,66) blended in for higher-tier flashes makes
    // the room feel lit by warm spots rather than fluorescent bulbs.
    const grad = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.radius);
    const goldR = Math.round(255 - (255 - 212) * f.tint);
    const goldG = Math.round(255 - (255 - 169) * f.tint);
    const goldB = Math.round(255 - (255 - 66)  * f.tint);
    grad.addColorStop(0,    `rgba(255,255,255,${a})`);
    grad.addColorStop(0.18, `rgba(${goldR},${goldG},${goldB},${a * 0.85})`);
    grad.addColorStop(0.55, `rgba(${goldR},${goldG},${goldB},${a * 0.25})`);
    grad.addColorStop(1,    'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(f.x, f.y, f.radius, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawBolts(ctx: CanvasRenderingContext2D, bolts: Bolt[], fade: number, r: number) {
  // Brighter outer glow at high tiers — gives the storm a gold halo.
  const glowAlpha = lerp(0.45, 0.85, r);
  const glowWidth = lerp(8, 14, r);
  const coreWidth = lerp(2.5, 4, r);
  ctx.save();
  for (const b of bolts) {
    const a = Math.max(0, b.life) * fade;
    ctx.strokeStyle = `rgba(212,169,66,${a * glowAlpha})`;
    ctx.lineWidth = glowWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    b.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();
    ctx.strokeStyle = `rgba(255,255,255,${a})`;
    ctx.lineWidth = coreWidth;
    ctx.beginPath();
    b.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();
  }
  ctx.restore();
}

/* ============================================================================
 * Particle drawing — branches by shape
 * ============================================================================ */

function drawParticle(ctx: CanvasRenderingContext2D, p: Particle, fade: number) {
  const a = Math.max(0, p.life) * fade;
  ctx.save();
  ctx.globalAlpha = a;
  ctx.translate(p.x, p.y);
  if (p.rot !== 0) ctx.rotate(p.rot);

  switch (p.shape) {
    case 'rect': {
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      break;
    }
    case 'streamer': {
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size * 1.5, p.size, p.size * 3);
      break;
    }
    case 'circle': {
      // Soft-edged flame look — radial gradient from core to transparent.
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, p.size);
      grad.addColorStop(0, p.color);
      grad.addColorStop(0.5, p.color);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(0, 0, p.size, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'spark': {
      // Bright dot with subtle outer glow for fireworks.
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, p.size * 2);
      grad.addColorStop(0, p.color);
      grad.addColorStop(0.4, p.color);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(0, 0, p.size * 2, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'coin': {
      // Gold coin: outer ring + inner gradient + sparkle highlight.
      const r = p.size;
      ctx.fillStyle = '#9c7d28'; // dark gold ring
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();
      const grad = ctx.createRadialGradient(-r * 0.35, -r * 0.35, 0, 0, 0, r);
      grad.addColorStop(0, GOLD_300);
      grad.addColorStop(0.5, GOLD_400);
      grad.addColorStop(1, GOLD_500);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.85, 0, Math.PI * 2);
      ctx.fill();
      // Sparkle highlight
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.beginPath();
      ctx.arc(-r * 0.35, -r * 0.35, r * 0.18, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'bill': {
      // Cash ticket — cream rectangle with a deep-gold border and a
      // gold "$" stamp. Reads as money without using off-brand green.
      const w = p.size * 1.7;
      const h = p.size;
      ctx.fillStyle = GOLD_DEEP;
      ctx.fillRect(-w / 2, -h / 2, w, h);
      ctx.fillStyle = CREAM;
      ctx.fillRect(-w / 2 + 1.5, -h / 2 + 1.5, w - 3, h - 3);
      ctx.fillStyle = GOLD_500;
      ctx.font = `bold ${Math.round(h * 0.7)}px Georgia`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('$', 0, 0);
      break;
    }
  }

  ctx.restore();
}
